import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, ArrowRight, Download, Share2, Trash2, Edit2, Clock, Brain, 
  Target, X, Check, Eye, Layers 
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format } from 'date-fns';

import type { Artifact, ClarityExport, DigState, Layer, Presence } from './lib/types';
import { LAYERS } from './lib/types';
import {
  addArtifact, addOpenQuestion, addStatement, brushArtifact, deleteArtifact,
  deleteEntireDig, generateCuriosityPrompts, moveArtifact, prepareClarityExport,
  removeOpenQuestion, removeStatement, resumeOrCreate, sealRevision, 
  updateArtifactText, updatePresence, computeSimpleStats,
} from './lib/store';

// ---------- Small beautiful primitives ----------
function Slider({ 
  label, value, onChange, min = 1, max = 5, help 
}: { 
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; help?: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <div className="text-[12px] font-medium tracking-wide text-earth-700">{label}</div>
        <div className="tabular-nums text-sm font-mono text-ink/70">{value} / {max}</div>
      </div>
      <input
        type="range" min={min} max={max} step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full accent-[#73614d]"
      />
      {help && <div className="text-[11px] text-earth-700/70">{help}</div>}
    </div>
  );
}

// ---------- Main App ----------
export default function Dig() {
  const [state, setState] = useState<DigState | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [resumptionNote, setResumptionNote] = useState<string | undefined>();

  // UI state
  const [activeLayer, setActiveLayer] = useState<Layer>('surface');
  const [newArtifactText, setNewArtifactText] = useState('');
  const [editingArtifact, setEditingArtifact] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [showCuriosity, setShowCuriosity] = useState(false);
  const [curiosityFor, setCuriosityFor] = useState<Layer | undefined>();
  const [selectedRevisionIds, setSelectedRevisionIds] = useState<string[]>([]);
  const [showExport, setShowExport] = useState(false);
  const [exportNote, setExportNote] = useState('');
  const [readerContext, setReaderContext] = useState('');
  const [showPresence, setShowPresence] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [sealing, setSealing] = useState(false);
  const [sealNarrative, setSealNarrative] = useState('');
  const [sealTitle, setSealTitle] = useState('');

  // Load or create
  useEffect(() => {
    const { state: s, isNew: n, resumptionNote: rn } = resumeOrCreate();
    setState(s);
    setIsNew(n);
    setResumptionNote(rn);
    if (s.revisions.length > 0) {
      setSelectedRevisionIds([s.revisions[s.revisions.length - 1].id]);
    }
  }, []);

  // Keyboard: / for command, esc to close modals
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        setCommandOpen(true);
      }
      if (e.key === 'Escape') {
        setCommandOpen(false);
        setShowCuriosity(false);
        setShowExport(false);
        setSealing(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!state) {
    return <div className="min-h-screen flex items-center justify-center text-earth-700">Loading your dig…</div>;
  }

  const stats = computeSimpleStats(state);
  const curiosityPrompts = generateCuriosityPrompts(state.artifacts, state.model, curiosityFor || activeLayer);

  // --- Core actions ---
  function persist(next: DigState) {
    setState(next);
  }

  function handleAddArtifact() {
    if (!newArtifactText.trim()) return;
    const { state: next } = addArtifact(state, activeLayer, newArtifactText);
    persist(next);
    setNewArtifactText('');
    
    // Immediately open curiosity loop — this is the "curiosity loop" mechanism
    setCuriosityFor(activeLayer);
    setShowCuriosity(true);
    
    // Gentle toast
    toast.success('Artifact placed. A small excavation.', { duration: 1400 });
  }

  function handleMove(artifactId: string, to: Layer) {
    const next = moveArtifact(state, artifactId, to);
    persist(next);
    toast(`Moved to ${LAYERS.find(l => l.key === to)!.label}`, { duration: 900 });
  }

  function startEdit(artifact: Artifact) {
    setEditingArtifact(artifact.id);
    setEditText(artifact.text);
  }

  function saveEdit() {
    if (!editingArtifact) return;
    const next = updateArtifactText(state, editingArtifact, editText);
    persist(next);
    setEditingArtifact(null);
    setEditText('');
  }

  function handleDeleteArtifact(id: string) {
    const next = deleteArtifact(state, id);
    persist(next);
    toast('Artifact removed from the dig.');
  }

  function handleBrush(id: string) {
    // Ritual: "sit with this for a moment"
    const seconds = 45 + Math.floor(Math.random() * 25);
    const next = brushArtifact(state, id, seconds);
    persist(next);
    toast(`You sat with it for ~${seconds}s. That counts.`, { description: 'Attention is the method.' });
  }

  function updatePresenceField(patch: Partial<Presence>) {
    const next = updatePresence(state, patch);
    persist(next);
  }

  function handleAddStatement() {
    const text = prompt('New statement about how you are holding this right now:');
    if (text) {
      const next = addStatement(state, text);
      persist(next);
    }
  }

  function handleAddQuestion() {
    const text = prompt('An open question you are willing to carry:');
    if (text) {
      const next = addOpenQuestion(state, text);
      persist(next);
    }
  }

  // The key ritual: sealing a revision of the self-model
  function openSealRevision() {
    setSealNarrative('');
    setSealTitle(state.title || '');
    setSealing(true);
  }

  function confirmSealRevision() {
    if (!sealNarrative.trim() && state.revisions.length === 0) {
      // First revision should have some reflection
      toast.error('A short note on what shifted helps future you.');
      return;
    }
    const { state: next, revision } = sealRevision(state, sealNarrative, sealTitle || undefined);
    persist(next);
    setSealing(false);
    setSealNarrative('');
    setSelectedRevisionIds([revision.id]);
    toast.success(`Revision ${revision.number} sealed.`, {
      description: 'This is now part of your revision log — the shareable unit.',
    });
  }

  // Export flow — the "self-model revision log" as beautiful shareable clarity
  function openExport() {
    if (state.revisions.length === 0) {
      toast('Seal at least one revision first. That is the point of the dig.');
      return;
    }
    setExportNote('');
    setReaderContext('');
    setShowExport(true);
  }

  function doPrepareExport() {
    const exp = prepareClarityExport(
      state,
      selectedRevisionIds,
      state.artifacts.map(a => a.id), // include all for richness; user can edit later in real version
      exportNote || undefined,
      readerContext || undefined
    );

    // Generate a beautiful standalone HTML document
    const html = generateShareableHTML(exp, state);
    
    // Download as self-contained file (real, useful)
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = (state.title || 'dig-revision').replace(/[^a-z0-9-]/gi, '-').toLowerCase();
    a.download = `${safeTitle}-${format(new Date(), 'yyyy-MM-dd')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Also copy a clean text version to clipboard for quick sharing
    const textVersion = generatePlainTextVersion(exp);
    navigator.clipboard.writeText(textVersion).then(() => {
      toast.success('Clarity export downloaded.', { 
        description: 'A clean text version is also in your clipboard.' 
      });
    });

    setShowExport(false);
  }

  function handleFullDelete() {
    deleteEntireDig();
    window.location.reload();
  }

  // Command palette actions
  function runCommand(cmd: string) {
    setCommandOpen(false);
    if (cmd === 'new-artifact') {
      const el = document.getElementById('artifact-input') as HTMLTextAreaElement | null;
      el?.focus();
    }
    if (cmd === 'seal') openSealRevision();
    if (cmd === 'export') openExport();
    if (cmd === 'presence') setShowPresence(true);
    if (cmd === 'curiosity') {
      setCuriosityFor(activeLayer);
      setShowCuriosity(true);
    }
    if (cmd === 'delete') setShowDeleteConfirm(true);
  }

  return (
    <div className="min-h-screen bg-[#f4f1ea] text-ink pb-24">
      <Toaster position="top-center" richColors closeButton />

      {/* Header — calm, human, with the required attribution */}
      <header className="border-b border-[#d9d3c6] bg-[#f9f7f3]/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-[#3d4d3d] flex items-center justify-center">
                <Layers className="w-4 h-4 text-[#f9f7f3]" />
              </div>
              <div>
                <div className="font-semibold tracking-tight text-lg leading-none">dig</div>
                <div className="text-[10px] text-earth-700 -mt-0.5">attention archeology</div>
              </div>
            </div>
            <div className="text-xs text-earth-700 pl-2 border-l border-[#d9d3c6]">
              climate anxiety without actionable agency
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => setShowPresence(true)} className="btn flex items-center gap-1.5 text-xs">
              <Brain className="w-3.5 h-3.5" /> presence
            </button>
            <button onClick={openSealRevision} className="btn flex items-center gap-1.5 text-xs">
              <Check className="w-3.5 h-3.5" /> seal revision
            </button>
            <button onClick={openExport} className="btn btn-primary flex items-center gap-1.5 text-xs">
              <Share2 className="w-3.5 h-3.5" /> prepare shareable clarity
            </button>
            <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-ghost text-xs text-clay-600 flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Gentle resumption / orientation line */}
        {(resumptionNote || isNew) && (
          <div className="max-w-5xl mx-auto px-6 pb-3 text-[12px] text-earth-700">
            {isNew ? (
              <>This is a new dig. Everything stays on your device until you choose to export or share a revision.</>
            ) : (
              <>{resumptionNote} Take your time.</>
            )}
          </div>
        )}
      </header>

      <div className="max-w-5xl mx-auto px-6 pt-8">
        {/* Hero intent + strong disclaimer */}
        <div className="max-w-2xl mb-8">
          <div className="uppercase tracking-[1.5px] text-[10px] text-earth-700 mb-1">A TOOL FOR PRIVATE CLARITY</div>
          <h1 className="serif text-[42px] leading-[1.05] tracking-[-1.2px] text-ink">
            Turn the tangle of climate response<br />into something you can actually hold.
          </h1>
          <p className="mt-3 text-[15px] text-earth-700 max-w-prose">
            This is not therapy. It does not diagnose or treat. It is a private excavation site for people who care deeply 
            and feel the absence of clear, meaningful agency. The goal is <span className="text-ink font-medium">sharable clarity</span>, not relief.
          </p>
          <div className="mt-3 text-[12px] disclaimer max-w-prose">
            Your data lives only in this browser until you explicitly export a revision log. 
            You can delete everything at any moment. No accounts. No cross-user analysis.
          </div>
        </div>

        {/* Presence summary bar (ethical capture, always visible and transparent) */}
        <div className="mb-6 flex flex-wrap gap-3 items-center text-sm">
          <div 
            onClick={() => setShowPresence(true)}
            className="presence inline-flex items-center gap-3 px-4 py-2 rounded-full cursor-pointer hover:bg-white border border-[#d9d3c6]"
          >
            <div className="flex items-center gap-1.5 text-earth-700">
              <Brain className="w-4 h-4" /> load <span className="font-mono tabular-nums text-ink">{state.currentPresence.workingMemoryLoad}</span>
            </div>
            <div className="flex items-center gap-1.5 text-earth-700">
              <Target className="w-4 h-4" /> control <span className="font-mono tabular-nums text-ink">{state.currentPresence.perceivedControl}</span>
            </div>
            <div className="text-[11px] text-earth-700/70 pl-1 border-l border-[#d9d3c6]">
              {state.currentPresence.contextSwitches} switches{state.currentPresence.resumedAfterHours ? ` • resumed after ${state.currentPresence.resumedAfterHours}h` : ''}
            </div>
            <div className="text-[11px] underline decoration-dotted ml-1">edit</div>
          </div>

          <div className="text-[12px] text-earth-700">
            {stats.totalArtifacts} artifacts • {stats.revisionCount} revisions sealed
          </div>
        </div>

        {/* THE DIG — Attention Archeology interface */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Layers / Strata */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-earth-700" />
                <div className="font-medium tracking-tight">The Dig — Attention Archeology</div>
              </div>
              <div className="text-[11px] text-earth-700">drag artifacts between layers</div>
            </div>

            <div className="space-y-2">
              {LAYERS.map((layerDef) => {
                const layerArtifacts = state.artifacts.filter(a => a.layer === layerDef.key);
                const isActive = activeLayer === layerDef.key;
                return (
                  <div
                    key={layerDef.key}
                    className={`layer p-3 ${isActive ? 'ring-1 ring-[#b8a68d] bg-white' : 'bg-white'}`}
                    onClick={() => setActiveLayer(layerDef.key)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-medium text-[15px]">{layerDef.label}</div>
                        <div className="text-[11px] text-earth-700">{layerDef.depth}</div>
                      </div>
                      <div className="text-[11px] tabular-nums text-earth-700/70 pr-1">
                        {layerArtifacts.length}
                      </div>
                    </div>

                    {/* Drop zone + artifacts */}
                    <div 
                      className={`min-h-[54px] rounded p-1.5 bg-[#faf8f3] border border-dashed ${isActive ? 'border-[#b8a68d]' : 'border-[#d9d3c6]'}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const id = e.dataTransfer.getData('text/plain');
                        if (id) handleMove(id, layerDef.key);
                      }}
                    >
                      {layerArtifacts.length === 0 && (
                        <div className="text-[12px] text-earth-700/60 py-2 px-2 italic">Nothing placed here yet. Add something small.</div>
                      )}
                      <div className="space-y-1.5">
                        {layerArtifacts.map(art => (
                          <div
                            key={art.id}
                            draggable
                            onDragStart={(e) => e.dataTransfer.setData('text/plain', art.id)}
                            className="artifact group px-3 py-2 text-[13.5px] leading-snug flex gap-2 items-start"
                          >
                            <div className="flex-1 pt-px">
                              {editingArtifact === art.id ? (
                                <div className="flex gap-2">
                                  <input
                                    className="input text-sm py-1"
                                    value={editText}
                                    onChange={e => setEditText(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') { setEditingArtifact(null); setEditText(''); } }}
                                    autoFocus
                                  />
                                  <button onClick={saveEdit} className="btn text-xs px-2">save</button>
                                </div>
                              ) : (
                                <div className="whitespace-pre-wrap">{art.text}</div>
                              )}
                              {art.brushedSeconds && (
                                <div className="mt-1 text-[10px] text-clay-600 flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> sat with for ~{art.brushedSeconds}s
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col gap-1 opacity-60 group-hover:opacity-100">
                              <button onClick={() => handleBrush(art.id)} title="Sit with this a moment" className="p-1 hover:bg-white rounded">
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => startEdit(art)} title="Edit" className="p-1 hover:bg-white rounded">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDeleteArtifact(art.id)} title="Remove" className="p-1 hover:bg-white rounded text-clay-600">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {isActive && (
                      <div className="mt-2 flex gap-2">
                        <textarea
                          id="artifact-input"
                          className="textarea input flex-1 text-[14px] py-2"
                          placeholder="Add a small, specific find… (a sentence, image, bodily sensation, memory fragment)"
                          value={newArtifactText}
                          onChange={e => setNewArtifactText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddArtifact(); } }}
                        />
                        <button onClick={handleAddArtifact} className="btn btn-primary self-end h-10 px-3">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-3 text-[11px] text-earth-700 px-1">
              Moving an artifact between layers is an act of restructuring attention. Do it slowly.
            </div>
          </div>

          {/* Right column: Self-Model + Curiosity + Revision Log */}
          <div className="lg:col-span-5 space-y-6">
            {/* Living Self-Model */}
            <div className="card p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-medium tracking-tight">Current Self-Model</div>
                <button onClick={handleAddStatement} className="btn text-xs">+ statement</button>
              </div>

              <div className="space-y-2">
                {state.model.statements.length === 0 && (
                  <div className="text-xs text-earth-700 italic">No statements yet. Add honest ones.</div>
                )}
                {state.model.statements.map((s, i) => (
                  <div key={i} className="group flex gap-2 items-start text-[13.5px] bg-[#faf8f3] px-3 py-2 rounded border border-[#d9d3c6]">
                    <div className="flex-1">{s}</div>
                    <button onClick={() => persist(removeStatement(state, i))} className="opacity-40 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="font-medium tracking-tight text-sm">Open questions you are willing to carry</div>
                <button onClick={handleAddQuestion} className="btn text-xs">+ question</button>
              </div>
              <div className="mt-2 space-y-1.5">
                {state.model.openQuestions.map((q, i) => (
                  <div key={i} className="flex gap-2 text-[13px] text-earth-700 pl-1">
                    <div className="flex-1">• {q}</div>
                    <button onClick={() => persist(removeOpenQuestion(state, i))} className="opacity-40 hover:opacity-100"><X className="w-3 h-3 mt-0.5" /></button>
                  </div>
                ))}
                {state.model.openQuestions.length === 0 && (
                  <div className="text-xs text-earth-700 pl-1 italic">These are allowed to stay open.</div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-[#d9d3c6] text-[10px] text-earth-700">
                Last revised {format(new Date(state.model.updatedAt), 'MMM d, yyyy')}
              </div>
            </div>

            {/* Curiosity Loops */}
            <div className="card p-5">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <div className="font-medium tracking-tight">Curiosity Loops</div>
                  <div className="text-[11px] text-earth-700">Small questions that help structure what feels formless.</div>
                </div>
                <button onClick={() => { setCuriosityFor(activeLayer); setShowCuriosity(true); }} className="btn text-xs">open</button>
              </div>
              <div className="text-[12px] text-earth-700/80">
                After you place something in a layer, a loop appears. Answering is optional. The goal is not completion — it is a slightly clearer next artifact.
              </div>
            </div>

            {/* Revision Log — the actual share/export unit */}
            <div className="card p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-medium tracking-tight">Self-Model Revision Log</div>
                <button onClick={openSealRevision} className="btn text-xs">seal new revision</button>
              </div>

              {state.revisions.length === 0 && (
                <div className="text-sm text-earth-700 py-2">No revisions sealed yet. When something has clarified, seal it. That becomes the thing you can share.</div>
              )}

              <div className="space-y-3 max-h-[340px] overflow-auto pr-1">
                {[...state.revisions].reverse().map(rev => {
                  const isSelected = selectedRevisionIds.includes(rev.id);
                  return (
                    <div 
                      key={rev.id} 
                      onClick={() => {
                        // toggle selection for export
                        if (isSelected) {
                          setSelectedRevisionIds(prev => prev.filter(id => id !== rev.id));
                        } else {
                          setSelectedRevisionIds(prev => [...prev, rev.id]);
                        }
                      }}
                      className={`revision p-3 rounded cursor-pointer transition-all ${isSelected ? 'ring-1 ring-[#73614d] bg-[#f4f1ea]' : 'hover:bg-[#f4f1ea]'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-sm">Revision {rev.number}{rev.title ? ` — ${rev.title}` : ''}</div>
                          <div className="text-[11px] text-earth-700">{format(new Date(rev.createdAt), 'MMM d, yyyy • HH:mm')}</div>
                        </div>
                        <div className="text-right text-[10px] text-earth-700 tabular-nums">
                          load {rev.presence.workingMemoryLoad} • control {rev.presence.perceivedControl}
                        </div>
                      </div>
                      <div className="mt-2 text-[13px] leading-snug text-ink/90">{rev.narrative}</div>
                      {rev.model.statements.length > 0 && (
                        <div className="mt-2 text-[11px] text-earth-700 line-clamp-2">
                          “{rev.model.statements[0]}”
                        </div>
                      )}
                      <div className="mt-1.5 text-[10px] text-clay-600">
                        {rev.artifactIds.length} artifacts considered • {rev.presence.contextSwitches} switches
                        {rev.presence.resumedAfterHours ? ` • resumed +${rev.presence.resumedAfterHours}h` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-[10px] text-earth-700 mt-3">
                Selected revisions will be included when you prepare a shareable clarity export.
              </div>
            </div>
          </div>
        </div>

        {/* Footer with prominent creator links as required */}
        <div className="mt-16 pt-8 border-t border-[#d9d3c6] text-[12px] text-earth-700 flex flex-col sm:flex-row gap-y-2 gap-x-6 items-start sm:items-center justify-between">
          <div>
            This tool was built by <span className="text-ink font-medium">Abhay Pratap Singh</span>. 
            It exists because private complexity deserves better containers than silence or performance.
          </div>
          <div className="flex gap-4">
            <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer" className="hover:text-ink underline decoration-dotted">
              linkedin.com/in/mindful-abhay
            </a>
            <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer" className="hover:text-ink underline decoration-dotted">
              buymeacoffee.com/abhaypratapsingh
            </a>
          </div>
        </div>
      </div>

      {/* Curiosity Loop Modal — the structuring mechanism */}
      <AnimatePresence>
        {showCuriosity && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowCuriosity(false)}>
            <motion.div 
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              className="curiosity max-w-lg w-full rounded-xl p-6 shadow-xl" onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-lg tracking-tight">A small curiosity</div>
                  <div className="text-xs text-earth-700">Answer only if it feels alive. You can ignore everything.</div>
                </div>
                <button onClick={() => setShowCuriosity(false)}><X /></button>
              </div>

              <div className="mt-4 space-y-3">
                {curiosityPrompts.map((p, idx) => (
                  <div key={idx} className="bg-white border border-[#d9c3a8] rounded-lg p-4 text-[14px]">
                    {p}
                    <div className="mt-3">
                      <button 
                        className="btn text-xs"
                        onClick={() => {
                          // Turn the answer into a new artifact in the current layer
                          const answer = prompt('Your response (optional but often useful):');
                          if (answer && answer.trim()) {
                            const { state: next } = addArtifact(state, curiosityFor || activeLayer, answer);
                            persist(next);
                            toast('Thank you. That became part of the dig.');
                          }
                          setShowCuriosity(false);
                        }}
                      >
                        answer as new find in this layer
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-[11px] text-earth-700">
                Curiosity loops exist to decide what deserves structure. They are not a quiz.
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Seal Revision Modal — deliberate, weighty, beautiful */}
      <AnimatePresence>
        {sealing && (
          <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" onClick={() => setSealing(false)}>
            <motion.div initial={{ scale: 0.985, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="card max-w-xl w-full p-6" onClick={e=>e.stopPropagation()}>
              <div className="font-medium text-xl tracking-tight mb-1">Seal a revision</div>
              <div className="text-sm text-earth-700 mb-4">
                This is the moment your current understanding becomes part of the permanent log. 
                Future you (and anyone you choose to share with) will see this exact state.
              </div>

              <input 
                className="input mb-3" 
                placeholder="Optional short title for this revision (e.g. “After the IPCC summary”)" 
                value={sealTitle} 
                onChange={e => setSealTitle(e.target.value)} 
              />

              <textarea 
                className="textarea input w-full mb-3" 
                placeholder="What shifted, even slightly? What became clearer or heavier? Write for future you." 
                value={sealNarrative} 
                onChange={e => setSealNarrative(e.target.value)}
              />

              <div className="text-[11px] text-earth-700 mb-4">
                The current presence readings (load {state.currentPresence.workingMemoryLoad}, control {state.currentPresence.perceivedControl}) will be attached to this revision. You can edit presence before sealing.
              </div>

              <div className="flex gap-2 justify-end">
                <button className="btn" onClick={() => setSealing(false)}>cancel</button>
                <button className="btn btn-primary" onClick={confirmSealRevision}>seal this revision</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Presence / Ethical Capture Modal */}
      <AnimatePresence>
        {showPresence && (
          <div className="fixed inset-0 bg-black/40 z-[90] flex items-center justify-center p-4" onClick={() => setShowPresence(false)}>
            <div className="card max-w-md w-full p-6" onClick={e=>e.stopPropagation()}>
              <div className="font-medium mb-1 flex items-center gap-2"><Brain className="w-4 h-4" /> How you are arriving right now</div>
              <div className="text-xs text-earth-700 mb-4">These numbers travel with every revision you seal. They are visible in exports. They are for pattern recognition by *you*.</div>

              <div className="space-y-5">
                <Slider 
                  label="Working memory load" 
                  value={state.currentPresence.workingMemoryLoad} 
                  onChange={v => updatePresenceField({ workingMemoryLoad: v })}
                  help="How full / occupied does your mind feel in this moment?"
                />
                <Slider 
                  label="Perceived control (over the things that matter here)" 
                  value={state.currentPresence.perceivedControl} 
                  onChange={v => updatePresenceField({ perceivedControl: v })}
                  help="Not global agency. Just the slice you are carrying today."
                />
                <div>
                  <div className="text-[12px] font-medium tracking-wide text-earth-700 mb-1.5">Context switches this sitting</div>
                  <div className="flex items-center gap-2">
                    <input type="number" min={0} className="input w-20" value={state.currentPresence.contextSwitches} onChange={e => updatePresenceField({ contextSwitches: parseInt(e.target.value) || 0 })} />
                    <button className="btn text-xs" onClick={() => updatePresenceField({ contextSwitches: (state.currentPresence.contextSwitches || 0) + 1 })}>+1 (I checked something else)</button>
                  </div>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-[#d9d3c6] text-[11px] text-earth-700">
                Nothing here is sent anywhere. It only helps you see your own capacity patterns across revisions.
              </div>

              <div className="mt-4 text-right">
                <button className="btn" onClick={() => setShowPresence(false)}>done</button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Export / Shareable Clarity Modal */}
      <AnimatePresence>
        {showExport && (
          <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={() => setShowExport(false)}>
            <div className="card max-w-2xl w-full p-6" onClick={e=>e.stopPropagation()}>
              <div className="font-medium text-xl tracking-tight">Prepare shareable clarity</div>
              <div className="text-sm text-earth-700 mt-1">You are choosing what to make visible. This is not “the whole story”. It is a deliberate slice.</div>

              <div className="mt-4">
                <div className="text-xs font-medium tracking-wider text-earth-700 mb-1">REVISIONS TO INCLUDE</div>
                <div className="flex flex-wrap gap-2">
                  {state.revisions.map(r => {
                    const sel = selectedRevisionIds.includes(r.id);
                    return (
                      <button key={r.id} onClick={() => {
                        setSelectedRevisionIds(sel 
                          ? selectedRevisionIds.filter(id => id !== r.id) 
                          : [...selectedRevisionIds, r.id]
                        );
                      }} className={`text-xs px-3 py-1 rounded border ${sel ? 'bg-[#3d4d3d] text-white border-[#3d4d3d]' : 'border-[#d9d3c6] hover:bg-white'}`}>
                        Rev {r.number}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3">
                <div>
                  <div className="text-xs font-medium tracking-wider text-earth-700 mb-1">NOTE FOR THE PERSON RECEIVING THIS (optional)</div>
                  <textarea className="input textarea" placeholder="This is a private note about where I was when I wrote these revisions..." value={exportNote} onChange={e=>setExportNote(e.target.value)} />
                </div>
                <div>
                  <div className="text-xs font-medium tracking-wider text-earth-700 mb-1">CONTEXT YOU WANT THE READER TO HAVE</div>
                  <textarea className="input textarea" placeholder="I am sharing this because..." value={readerContext} onChange={e=>setReaderContext(e.target.value)} />
                </div>
              </div>

              <div className="mt-5 flex gap-2 justify-end">
                <button className="btn" onClick={() => setShowExport(false)}>cancel</button>
                <button className="btn btn-primary" onClick={doPrepareExport} disabled={selectedRevisionIds.length === 0}>
                  <Download className="w-4 h-4" /> download beautiful standalone HTML + copy text
                </button>
              </div>

              <div className="mt-3 text-[10px] text-earth-700">The downloaded file is completely self-contained. You can host it, email it, or print it. No external calls.</div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Nuclear delete confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
            <div className="card max-w-sm w-full p-6 text-center" onClick={e=>e.stopPropagation()}>
              <div className="font-medium text-lg mb-2">Delete this entire dig?</div>
              <div className="text-sm text-earth-700">All artifacts, revisions, and presence notes will be gone from this browser. This cannot be undone.</div>
              <div className="flex gap-3 mt-6 justify-center">
                <button className="btn" onClick={() => setShowDeleteConfirm(false)}>keep everything</button>
                <button className="btn btn-danger" onClick={handleFullDelete}>yes, delete the whole thing</button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Command palette (modern web app bell) */}
      <AnimatePresence>
        {commandOpen && (
          <div className="fixed inset-0 z-[120] bg-black/40 flex items-start justify-center pt-[18vh]" onClick={() => setCommandOpen(false)}>
            <div className="card w-full max-w-md overflow-hidden" onClick={e=>e.stopPropagation()}>
              <div className="p-2">
                <div className="text-[11px] px-3 pt-2 pb-1 text-earth-700">COMMANDS</div>
                {[
                  { label: 'Add artifact in current layer', cmd: 'new-artifact' },
                  { label: 'Open curiosity loop', cmd: 'curiosity' },
                  { label: 'Seal a new revision', cmd: 'seal' },
                  { label: 'Prepare shareable clarity export', cmd: 'export' },
                  { label: 'Edit presence / load / control', cmd: 'presence' },
                  { label: 'Delete this entire dig', cmd: 'delete' },
                ].map(item => (
                  <button key={item.cmd} onClick={() => runCommand(item.cmd)} className="w-full text-left px-3 py-2.5 hover:bg-[#f4f1ea] rounded flex items-center justify-between text-sm">
                    {item.label}
                    <ArrowRight className="w-3.5 h-3.5 opacity-40" />
                  </button>
                ))}
              </div>
              <div className="border-t border-[#d9d3c6] px-3 py-2 text-[10px] text-earth-700">Press / anytime. Escape to close.</div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- Beautiful self-contained shareable HTML generator ----------
function generateShareableHTML(exp: ClarityExport, _originalState?: DigState): string {
  const revs = exp.selectedRevisions;
  const date = format(new Date(exp.exportedAt), 'MMMM d, yyyy');

  const revBlocks = revs.map(r => {
    const pres = r.presence;
    return `
      <div style="margin-bottom:28px; border-left:3px solid #73614d; padding-left:16px;">
        <div style="font-size:11px; letter-spacing:0.6px; color:#73614d; margin-bottom:4px;">REVISION ${r.number}${r.title ? ` — ${r.title}` : ''} &nbsp;·&nbsp; ${format(new Date(r.createdAt), 'MMM d, yyyy')}</div>
        <div style="font-size:15px; line-height:1.55; color:#1c1917; margin:6px 0 10px;">${escapeHtml(r.narrative)}</div>
        
        <div style="font-size:13px; color:#3d4d3d; margin-bottom:6px;"><strong>Self-model at this time</strong></div>
        <ul style="margin:0 0 12px 16px; padding:0; font-size:13.5px; line-height:1.5;">
          ${r.model.statements.map(s => `<li style="margin-bottom:3px;">${escapeHtml(s)}</li>`).join('')}
        </ul>
        
        ${r.model.openQuestions.length ? `
        <div style="font-size:12.5px; color:#73614d; margin-bottom:4px;">Open questions carried:</div>
        <ul style="margin:0 0 14px 16px; font-size:12.5px; color:#5f5244;">
          ${r.model.openQuestions.map(q => `<li>${escapeHtml(q)}</li>`).join('')}
        </ul>` : ''}

        <div style="font-size:11px; color:#8c6642; border-top:1px solid #d9c3a8; padding-top:7px; margin-top:6px;">
          Presence when sealed — working memory load: ${pres.workingMemoryLoad}/5 &nbsp;·&nbsp; perceived control: ${pres.perceivedControl}/5 &nbsp;·&nbsp; context switches: ${pres.contextSwitches}${pres.resumedAfterHours ? ` &nbsp;·&nbsp; resumed after ${pres.resumedAfterHours}h` : ''}
        </div>
      </div>
    `;
  }).join('');

  const currentModelBlock = `
    <div style="margin-top:32px; padding-top:20px; border-top:1px solid #d9d3c6;">
      <div style="font-size:11px; letter-spacing:0.6px; color:#73614d; margin-bottom:6px;">CURRENT SELF-MODEL (AT TIME OF EXPORT)</div>
      <ul style="font-size:14px; line-height:1.6; margin-left:4px;">
        ${exp.currentModel.statements.map(s => `<li style="margin-bottom:4px;">${escapeHtml(s)}</li>`).join('')}
      </ul>
    </div>
  `;

  const contextBlock = exp.contextForReader || exp.shareNote ? `
    <div style="margin:32px 0 12px; padding:14px 16px; background:#f9f7f3; border:1px solid #d9d3c6; font-size:13px; line-height:1.55;">
      ${exp.contextForReader ? `<div style="margin-bottom:8px;"><strong>Context from the person who made this:</strong><br>${escapeHtml(exp.contextForReader)}</div>` : ''}
      ${exp.shareNote ? `<div>${escapeHtml(exp.shareNote)}</div>` : ''}
    </div>
  ` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>dig — ${exp.digTitle || 'revision log'} — ${date}</title>
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  body { font-family: Georgia, 'Iowan Old Style', serif; line-height:1.65; color:#1c1917; background:#f9f7f3; margin:0; padding:40px 20px; }
  .container { max-width: 680px; margin: 0 auto; }
  h1 { font-size: 28px; margin:0 0 6px; letter-spacing:-0.6px; }
  .meta { font-size:12px; color:#73614d; margin-bottom:32px; letter-spacing:0.3px; }
  .disclaimer { font-size:10.5px; color:#73614d; border-top:1px solid #d9d3c6; padding-top:14px; margin-top:40px; }
  ul { padding-left:18px; }
  .footer { margin-top:50px; font-size:10px; color:#8c6642; }
</style>
</head>
<body>
  <div class="container">
    <div style="margin-bottom:8px; font-size:10px; letter-spacing:1.5px; color:#8c6642;">DIG — ATTENTION ARCHEOLOGY</div>
    <h1>${escapeHtml(exp.digTitle || 'My Climate Response')}</h1>
    <div class="meta">Revision log exported ${date} • ${revs.length} revision${revs.length === 1 ? '' : 's'} included</div>

    ${revBlocks}
    ${currentModelBlock}
    ${contextBlock}

    <div class="disclaimer">
      This document was generated by a private reflective tool. It is not a diagnosis, a clinical record, or a complete picture. 
      It is one person’s attempt to turn private complexity into something slightly more shareable. 
      Nothing here was created by or sent to any server at the time of export.
    </div>

    <div class="footer">
      dig — built by Abhay Pratap Singh<br>
      <a href="https://www.linkedin.com/in/mindful-abhay/" style="color:#8c6642;">linkedin</a> · 
      <a href="https://buymeacoffee.com/abhaypratapsingh" style="color:#8c6642;">buy me a coffee</a>
    </div>
  </div>
</body>
</html>`;
}

function generatePlainTextVersion(exp: ClarityExport): string {
  let out = `DIG — SELF-MODEL REVISION LOG\nExported ${format(new Date(exp.exportedAt), 'MMMM d, yyyy')}\n\n`;
  exp.selectedRevisions.forEach(r => {
    out += `REVISION ${r.number}${r.title ? ` — ${r.title}` : ''}\n`;
    out += `${format(new Date(r.createdAt), 'MMM d, yyyy')}\n\n`;
    out += `${r.narrative}\n\n`;
    out += `Self-model:\n${r.model.statements.map(s => `- ${s}`).join('\n')}\n\n`;
    if (r.model.openQuestions.length) {
      out += `Open questions:\n${r.model.openQuestions.map(q => `- ${q}`).join('\n')}\n\n`;
    }
    const p = r.presence;
    out += `Presence: load ${p.workingMemoryLoad}/5, control ${p.perceivedControl}/5, switches ${p.contextSwitches}\n`;
    out += `---\n\n`;
  });
  out += `\n(current model at export)\n${exp.currentModel.statements.map(s => `- ${s}`).join('\n')}\n\n`;
  if (exp.contextForReader) out += `Context: ${exp.contextForReader}\n`;
  out += `\n---\nCreated with dig by Abhay Pratap Singh\nhttps://www.linkedin.com/in/mindful-abhay/`;
  return out;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
